import { useSortable } from "@dnd-kit/sortable"
import type { KeyboardEventHandler } from "react"

import { type HeaderKeyDown, mergeHeaderKeyDown } from "./entity-table.utils"

/**
 * The sortable half of a draggable column header, shared by the two header
 * cells that have one.
 *
 * Composing the node ref is left to the caller: the two differ on whether a
 * non-reorderable cell still registers one.
 */
export function useReorderableHeader(
  columnKey: string | undefined,
  fromTable: HeaderKeyDown,
  reorderable: boolean
) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting
  } = useSortable({
    id: columnKey ?? "__static__",
    // dnd-kit defaults a draggable to role="button", and `aria-sort` is only
    // defined on a column header — the default leaves it meaningless.
    attributes: { role: "columnheader" }
  })

  const { onKeyDown: startDrag, ...dragListeners } = listeners ?? {}

  const dragProps = reorderable
    ? {
        ...attributes,
        ...dragListeners,
        onKeyDown: mergeHeaderKeyDown(
          fromTable,
          // dnd-kit types every synthetic listener as a bare `Function`.
          startDrag as KeyboardEventHandler<HTMLTableCellElement> | undefined,
          { dragInProgress: isSorting }
        )
      }
    : {}

  return { setNodeRef, dragProps, isDragging, transform, transition }
}
